"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import { motion } from "framer-motion";
import {
  Boxes,
  Brain,
  CheckCircle2,
  Clock,
  Database,
  GitBranch,
  Layers,
  Network,
  Search,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import * as React from "react";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import integrationImage from "@/public/images/integration.jpg";

const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

const GRAPH_SNIPPET = `from kautious import KautiousClient

client = KautiousClient(api_url="...", token="...")
client.enable(openai=True, anthropic=True)`;

export default function GraphPage() {
  const { theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const isLight = mounted && theme === "light";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        {/* Hero Section with Background */}
        <section className="relative w-full overflow-hidden min-h-[85vh] flex items-center justify-center">
          <div className="absolute inset-0 z-0">
            <Image
              src={integrationImage}
              alt="Temporal knowledge graph visualization"
              className="object-cover object-center w-full h-full opacity-40 dark:opacity-30"
              fill
              priority
              sizes="100vw"
              quality={90}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-teal-900/20 via-emerald-900/40 to-background/95" />
          </div>

          <div className="absolute inset-0 bg-grid-white/10 [mask-image:radial-gradient(white,transparent_80%)] pointer-events-none z-10 opacity-30" />

          <motion.div
            className="absolute top-20 left-8 w-16 h-16 bg-teal-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-teal-500/20 z-10"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Network className="w-8 h-8 text-teal-500/70" />
          </motion.div>
          <motion.div
            className="absolute top-32 right-12 w-14 h-14 bg-emerald-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-emerald-500/20 z-10"
            style={{ animationDelay: "1s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Brain className="w-7 h-7 text-emerald-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-32 left-16 w-14 h-14 bg-cyan-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-cyan-500/20 z-10"
            style={{ animationDelay: "2s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Clock className="w-7 h-7 text-cyan-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-20 right-20 w-14 h-14 bg-green-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-green-500/20 z-10"
            style={{ animationDelay: "0.5s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <GitBranch className="w-7 h-7 text-green-500/70" />
          </motion.div>

          <div className="relative z-20 section-container">
            <div className="flex flex-col items-center text-center max-w-5xl mx-auto px-4 py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span
                  className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium mb-6 backdrop-blur-sm"
                  style={{
                    backgroundColor: isLight
                      ? "rgba(17, 24, 39, 0.2)"
                      : "rgba(255, 255, 255, 0.2)",
                    color: isLight ? "#111827" : "#ffffff",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: isLight
                      ? "rgba(17, 24, 39, 0.3)"
                      : "rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <Network
                    className="w-4 h-4 mr-2"
                    style={{ color: isLight ? "#111827" : "#ffffff" }}
                  />
                  Kautious Graph
                </span>
                <h1
                  className="mb-6"
                  style={{ color: isLight ? "#111827" : "#ffffff" }}
                >
                  Give your AI agents a memory worth trusting.
                </h1>
                <p
                  className="text-xl mb-10 leading-relaxed max-w-4xl"
                  style={{
                    color: isLight ? "#374151" : "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  Kautious Graph turns conversations, decisions, documents, and
                  workflows into a temporal knowledge graph your agents can
                  actually use. No more stateless copilots. No more vector
                  search roulette. Build agents that remember what happened, why
                  it mattered, who decided it, and what changed.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button
                    size="lg"
                    className="hover:opacity-90"
                    style={{
                      backgroundColor: isLight ? "#0d9488" : "#14b8a6",
                      color: "#ffffff",
                    }}
                    asChild
                  >
                    <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                      Request early access
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="hover:opacity-80"
                    style={{
                      borderColor: isLight ? "#111827" : "#ffffff",
                      color: isLight ? "#111827" : "#ffffff",
                      backgroundColor: "transparent",
                    }}
                    asChild
                  >
                    <Link href="#how-it-works">See how it works</Link>
                  </Button>
                </div>
                <p
                  className="mt-6 text-sm"
                  style={{
                    color: isLight ? "#4b5563" : "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  Private onboarding for teams building production agent
                  systems.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Enemy */}
        <section className="section-padding bg-secondary/5">
          <div className="section-container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Vector databases were built for search. You were sold them as
              memory.
            </h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-3xl mx-auto">
              Similarity search can find related text. It does not understand
              what changed last Tuesday, why an agent approved the refund, or
              that "the client" in March and "Acme Corp" in June are the same
              entity with a renegotiated contract in between. Stuffing the
              context window is not remembering. It is a workaround for systems
              that forget.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {[
                {
                  word: "Who",
                  body: "Entities and relationships, extracted automatically and deduplicated over time.",
                },
                {
                  word: "When",
                  body: "Facts that know when they became true and when they stopped being true.",
                },
                {
                  word: "Why",
                  body: "Decisions traced back to inputs, policies, citations, approvals, and outcomes.",
                },
              ].map((item) => (
                <Card key={item.word} className="p-6 card-hover text-left">
                  <h3 className="text-xl font-bold mb-2 text-teal-600">
                    {item.word}
                  </h3>
                  <p className="text-muted-foreground text-sm">{item.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-2xl font-semibold">
              RAG retrieves.{" "}
              <span className="text-teal-600">Kautious remembers.</span>
            </p>
          </div>
        </section>

        {/* The First Win */}
        <section className="section-padding">
          <div className="section-container max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Memory in three lines.
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Every supported LLM call your app makes is now recorded.
                Conversations become episodes. Entities and relationships become
                graph structure.
              </p>
            </div>
            <div className="rounded-xl border border-border bg-muted/40 overflow-hidden mb-8">
              <div className="px-4 py-2 border-b border-border bg-muted/60 text-xs font-mono text-muted-foreground">
                python
              </div>
              <pre className="p-5 overflow-x-auto text-sm">
                <code className="font-mono text-foreground/90">
                  {GRAPH_SNIPPET}
                </code>
              </pre>
            </div>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto">
              The first time your agent says, "Last time, you decided to ship
              the v2 schema, and these were the remaining blockers," the value
              is obvious. Not a dashboard tour. Not a six-week data project. A
              working memory loop your team can feel immediately.
            </p>
          </div>
        </section>

        {/* Feature Sections */}
        <section className="section-padding bg-muted/30">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Vector stores remember text. Kautious Graph remembers meaning.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: Clock,
                  title: "Facts with a timeline, not a vibe",
                  body: "Every fact is temporal. Ask what your agent knew on March 3rd and get the March 3rd answer — not today's truth retrofitted into yesterday's context.",
                },
                {
                  icon: Layers,
                  title: "The right memories, sized to your budget",
                  body: "Smart context assembly classifies the task, ranks relevant memory, tracks work state, and packs the right context into the token budget you choose.",
                },
                {
                  icon: ShieldCheck,
                  title: "Receipts for every important decision",
                  body: "Decision traces capture inputs, policies, citations, exceptions, approvals, and outcomes. Answer 'why did the agent act?' with evidence, not archaeology.",
                },
                {
                  icon: Workflow,
                  title: "MCP-native agent memory",
                  body: "Connect to assistants and agent runtimes through MCP. Agents store memories, search context, retrieve episodes, inspect facts, and evaluate policies.",
                },
                {
                  icon: Boxes,
                  title: "Knowledge shaped to your domain",
                  body: "Domain packs specialize memory for the language of your business. Start with a known domain or generate a pack from a plain-English description.",
                },
                {
                  icon: Database,
                  title: "Built like infrastructure, because it is",
                  body: "Multi-graph tenancy, org and per-user isolation, OAuth 2.1, scoped access, first-party API keys, signed webhooks, and REST, SDK, and MCP interfaces.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="p-8 card-hover">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.body}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="section-padding">
          <div className="section-container max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                From raw interaction to usable memory.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                {
                  step: "1",
                  title: "Capture",
                  body: "Send conversations, documents, tickets, messages, app events, or decisions through REST, SDK, or MCP.",
                },
                {
                  step: "2",
                  title: "Structure",
                  body: "Kautious extracts entities, relationships, facts, timestamps, citations, work state, and decision traces.",
                },
                {
                  step: "3",
                  title: "Retrieve",
                  body: "Agents search the graph for relevant memory, temporal context, prior decisions, policies, and current work state.",
                },
                {
                  step: "4",
                  title: "Act",
                  body: "Your agent responds with continuity, context, and evidence instead of generic guesses.",
                },
              ].map((item) => (
                <Card key={item.step} className="p-6 card-hover">
                  <span className="text-3xl font-serif text-teal-600/40">
                    {item.step}
                  </span>
                  <h3 className="text-lg font-semibold mt-3 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{item.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Developer Section */}
        <section className="section-padding bg-muted/30">
          <div className="section-container max-w-4xl mx-auto text-center">
            <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
              <Search className="w-6 h-6 text-teal-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Built for teams shipping real agent products.
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Kautious Graph fits into the stack you already have instead of
              asking you to rebuild around it.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "REST API",
                "Python SDK (OpenAI · Anthropic · LiteLLM)",
                "MCP server",
                "OAuth 2.1 + first-party API keys",
                "Multi-tenant, multi-graph isolation",
                "Signed webhooks",
                "Temporal queries",
                "Decision traces & citations",
              ].map((item) => (
                <span
                  key={item}
                  className="text-sm text-foreground/80 border border-border bg-background rounded-full px-4 py-2"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="section-padding bg-gradient-to-br from-teal-600 to-emerald-600 text-white">
          <div className="section-container text-center">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-4 text-white/90">
                <Sparkles className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-[0.2em]">
                  Private design-partner access
                </span>
              </div>
              <h2 className="text-4xl font-semibold mb-6 text-white">
                Your agent shouldn't meet the same user for the first time
                twice.
              </h2>
              <p className="text-xl mb-8 text-white/90">
                We onboard design partners in focused cohorts — wiring the
                platform into your agent stack and tuning memory to your domain.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-teal-600 hover:bg-white/90"
                  asChild
                >
                  <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                    Request early access
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link href="/daytta">See it inside Daytta</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex justify-between items-center">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
